import React, { FC, useMemo, useState, useEffect } from 'react';
import { produce } from 'immer';
import { Theme } from '@withjoy/joykit';
import { ThemeProvider } from '@withjoy/joykit/theme';
import { GuestSiteTypographyOverrideProps, OverrideTypography } from './GuestSiteTypographyOverride.types';
import { useEventDesignThemingProvider } from '@shared/core/website/theming';
import { toUnitless } from '@shared/joykit/packages/core/common/themes/shared/typography';
import { GuestSiteEvent } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import { layoutConfigs } from '@apps/guest/packages/layout-engine/layouts/layout.constants';

const overrideTypography = (
  currentTheme: Theme,
  override: OverrideTypography,
  defaultFontFamily: string | undefined,
  eventDesign: GuestSiteEvent['eventDesign'] | undefined,
): Theme => {
  if (!eventDesign) {
    return currentTheme;
  }

  return produce(currentTheme, draft => {
    override.forEach(({ category, variant }) => {
      const fontConfig = eventDesign.fonts?.find(font => font.category === category);
      const variantDraft = draft.typography.variants[variant];

      if (variantDraft) {
        // Apply font family override
        variantDraft.fontFamily = fontConfig?.font?.fontFamily || defaultFontFamily || variantDraft.fontFamily;

        // Apply font size percentage override
        if (fontConfig?.sizePercent) {
          const baseFontSize = toUnitless(currentTheme.typography.variants[variant].fontSize);
          variantDraft.fontSize = `${baseFontSize * (fontConfig.sizePercent / 100)}rem`;
        }
      }
    });
  });
};

/**
 * GuestSiteTypographyOverride - Applies font pack category overrides to specific typography variants
 *
 * This component creates a nested JoyKitThemeProvider with category-specific font overrides.
 * It waits for fonts to be loaded (via EventDesignThemingProvider) before applying the theme to prevent FOUT.
 *
 * Example: EVENT_TITLE category font overrides the display6 variant used for hero titles
 *
 *
 * TODO:
 * Need to optimize this component -- there are 133 instances of this component and they each independently manage their own theme.
 * Should consolidate the typography overrides in a single theme context.
 */
export const GuestSiteTypographyOverride: FC<GuestSiteTypographyOverrideProps> = props => {
  const { override, defaultFontFamily, children } = props;

  // Get font loading state from provider
  const { eventDesign, hasFont, loadedFonts } = useEventDesignThemingProvider();

  const categories = useMemo(() => override.map(o => o.category), [override]);
  const layoutConfig = useMemo(() => (eventDesign?.websiteLayout.layoutType === 'brannan' ? layoutConfigs.brannan : layoutConfigs.aloha), [eventDesign?.websiteLayout.layoutType]);

  // Initialize theme with current eventDesign
  const [theme, setTheme] = useState<Theme>(() =>
    overrideTypography(eventDesign ? layoutConfig.configureThemeOverrides(eventDesign, layoutConfig.theme) : layoutConfig.theme, override, defaultFontFamily, eventDesign),
  );

  // Compute target theme (with category overrides)
  const themeValue = useMemo(() => {
    if (eventDesign) {
      const currentTheme = layoutConfig.configureThemeOverrides(eventDesign, layoutConfig.theme);
      return overrideTypography(currentTheme, override, defaultFontFamily, eventDesign);
    }
    return layoutConfig.theme;
  }, [defaultFontFamily, eventDesign, layoutConfig, override]);

  // Only update theme when all category fonts are loaded
  useEffect(() => {
    if (eventDesign) {
      const fontsForCategories = eventDesign.fonts
        ?.filter(font => categories.includes(font.category))
        .map(font => font.font?.fontFamily)
        .filter(Boolean) as string[];

      const unloadedFonts = fontsForCategories.filter(font => !hasFont(font));

      // Only apply overrides if all category fonts are loaded
      if (unloadedFonts.length === 0) {
        setTheme(themeValue);
      }
      // Otherwise: keep current theme to prevent FOUT
      // Old fonts remain in DOM (EventDesignThemingProvider accumulates fonts), so no flicker
    }
  }, [categories, eventDesign, hasFont, loadedFonts, themeValue]);

  return eventDesign?.fonts ? <ThemeProvider theme={theme}>{children}</ThemeProvider> : <>{children}</>;
};
